import { useMemo } from "react";
import {
    alignPeriods,
    AlignedPeriod,
    buildGrid,
    PlanGrid,
    PlanScale,
} from "modules/spending/workload-plan/models/plan-scale";
import { ProjectPlanSnapshot } from "../models/project-plan-types";

export interface ProjectPlanGrid {
    grid: PlanGrid;
    periods: AlignedPeriod[];
    /** Левые края разделителей месяцев, px */
    monthBreaks: number[];
    /** Сколько колонок реально попало в сетку — по ним обрезаются отрезки */
    columnsCount: number;
}

/**
 * Сетка страницы: видимая часть колонок, слоты выбранного масштаба и раскладка месяцев.
 *
 * Видимость считается по periodIndex, а не по флагу hidden: с сервера грузится
 * максимум обоих масштабов, поэтому и переключение день/неделя, и уменьшение
 * глубины показа проходят без запроса — достаточно пересобрать сетку.
 */
export function useProjectPlanGrid(
    plan: ProjectPlanSnapshot,
    scale: PlanScale,
    monthsOnPage: number
): ProjectPlanGrid {
    const columns = useMemo(
        () => plan.columns.filter((column) => column.periodIndex < monthsOnPage),
        [plan.columns, monthsOnPage]
    );

    const periods = useMemo(() => plan.periods.slice(0, monthsOnPage), [plan.periods, monthsOnPage]);

    const grid = useMemo(() => buildGrid(columns, scale, periods), [columns, scale, periods]);

    const aligned = useMemo(() => alignPeriods(periods, grid), [periods, grid]);

    const monthBreaks = useMemo(() => aligned.slice(1).map((period) => period.left), [aligned]);

    return { grid, periods: aligned, monthBreaks, columnsCount: columns.length };
}
